"use client";
import { getFiatCurrencies } from "@/app/consts/Currencies";
import { addCurrencyToStorage } from "@/app/utils/services/service/localStorageService";
import { useTranslations } from "next-intl";
import React, {
  useEffect,
  useState,
  type ChangeEvent,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { Cross, SearchIcon } from "../Icons";
import { Modal } from "../modal";
import CurrencyImage from "./CurrencyImage";

type CurrencyOption = {
  label: string;
  value: string;
  type?: string;
  name?: string;
};

type Props = {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  setUpdateCurrency: (op: CurrencyOption) => void;
  currency: {
    label?: string;
    value?: string;
  };
  currencies: CurrencyOption[];
};

export const CurrencyModal = ({
  currency,
  setUpdateCurrency,
  openModal,
  setOpenModal,
  currencies,
}: Props) => {
  const t = useTranslations();
  const [filteredCurrencies, setFilteredCurrencies] = useState(currencies);
  const [showAllFiatCurrencies, setShowAllFiatCurrencies] = useState(false);

  const handleSelectCurrency = (op: CurrencyOption, icon: ReactNode) => {
    setUpdateCurrency(op);
    setOpenModal(false);
  };

  const flatCurrencies = filteredCurrencies?.filter(
    (item) => item?.type == "FIAT"
  );
  const cryptoCurrencies = filteredCurrencies?.filter(
    (item) => item?.type == "CRYPTO"
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const filteredCurrencies = currencies?.filter(
        (item) =>
          item?.name?.toLowerCase().includes(value.toLowerCase()) ||
          item?.label?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCurrencies(filteredCurrencies);
    } else {
      setFilteredCurrencies(currencies);
    }
  };

  return (
    <Modal
      isOpen={openModal}
      width="w-full md:w-[825px] sm:w-11/12 !rounded-2xl sm:h-[640px] h-screen !m-0 !shadow-none"
      height="sm:p-8 pt-3 sm:px-6"
      innerClassName="w-full sm:m-0.5 sm:rounded-2xl p-0 sm:pt-0 pt-5 rounded-none"
      bgColor="bg-lightwhite dark:bg-sleap-gray-950 shadow"
    >
      <div>
        <div className=" mt-2  w-full bg-white dark:bg-sleap-gray-950">
          <div className="flex flex-row justify-between sm:gap-6 gap-4 w-full">
            <h3 className="text-2xl mb-0 font-switzer font-medium  dark:text-white text-sleap-gray-950">
              {t("Other.currencies")}
            </h3>
            <span
              className="sm:-mt-2 sm:-me-1 cursor-pointer"
              onClick={() => setOpenModal(false)}
            >
              <span className="dark:block hidden">
                <Cross color="#fff" />
              </span>
              <span className="dark:hidden block">
                <Cross color="lightblack" />
              </span>
            </span>
          </div>
          <div className="relative mt-4">
            <span className="absolute top-1/2 left-4 transform -translate-y-1/2 h-6 w-6 dark:block hidden">
              <SearchIcon />
            </span>
            <span className="absolute top-1/2 left-4 transform -translate-y-1/2 h-6 w-6 dark:hidden block">
              <SearchIcon color="lightblack" />
            </span>

            <input
              type="text"
              className="w-full pe-4 py-2 rounded-lg bg-lightwhite dark:bg-sleap-gray-950 border border-1 border-sleap-gray-400 focus-visible:border-sleap-gray-400 focus-visible:outline-none ps-11 font-inter text-base font-normal dark:text-sleap-gray-100 text-sleap-gray-800 currency-search dark:hover:border-white hover:border-text-sleap-gray-800 dark:placeholder:text-sleap-gray-100 placeholder:text-sleap-gray-800"
              placeholder={t("Other.search")}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-4 mt-4 pb-8 sm:pb-6 overflow-y-scroll gallery-modal sm:h-[45vh] h-[80vh]">
          {flatCurrencies.length ? (
            <div>
              <p className="mb-4 text-base font-bold dark:text-lightwhite text-sleap-gray-950 font-inter">
                {t("Other.fiatCurrencies")}
              </p>
              <div className="grid md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 place-items-center">
                {flatCurrencies
                  .slice(0, showAllFiatCurrencies ? flatCurrencies?.length : 8)
                  ?.map((item) => {
                    const fiatCurrency = getFiatCurrencies(t)?.find(
                      (c) => c?.label == item?.value
                    );
                    return (
                      <div
                        key={item?.label}
                        className={`cursor-pointer p-4 pe-3 flex gap-x-2 sm:w-[172px] w-full h-[66px] justify-start items-center text-base font-medium rounded-2xl font-inter dark:bg-sleap-gray-850 bg-white 
                        ${currency?.label === item?.label ? "border-3 dark:border-lightwhite border-sleap-gray-950" : "border dark:border-sleap-gray-600 border-sleap-gray-200"} 
                        text-lightwhite dark:hover:border-sleap-gray-600 dark:hover:bg-sleap-gray-700  hover:bg-sleap-gray-200
                        shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),_0px_1px_2px_-1px_rgba(0,0,0,0.10)] 
                        transition-all`}
                        onClick={() =>
                          handleSelectCurrency(
                            {
                              label: item?.label,
                              value: item?.value,
                            },
                            fiatCurrency?.icon
                          )
                        }
                      >
                        <span>{fiatCurrency?.icon}</span>
                        <span className="flex flex-col">
                          <p className="text-xs font-medium font-inter dark:text-sleap-gray-100 text-sleap-gray-800 mb-0">
                            {fiatCurrency?.description?.slice(0, 12)}
                            {Number(fiatCurrency?.description?.length) > 12
                              ? "..."
                              : ""}
                          </p>
                          <span className="dark:text-lightwhite text-sleap-gray-950 font-sm font-bold font-inter">
                            {fiatCurrency?.name}
                          </span>
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : (
            ""
          )}
          {!showAllFiatCurrencies && (
            <div className="mx-auto">
              <p
                className="text-center my-2 underline font-semibold text-base w-fit cursor-pointer"
                onClick={() => setShowAllFiatCurrencies(true)}
              >
                {t("Other.showMoreCurrencies")}
              </p>
            </div>
          )}
          {cryptoCurrencies.length ? (
            <div>
              <p className="mb-4 text-base font-bold dark:text-lightwhite text-sleap-gray-950 font-inter">
                {t("Other.cryptocurrencies")}
              </p>
              <div className="grid md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 place-items-center">
                {cryptoCurrencies?.map((item) => (
                  <div
                    key={item?.label}
                    className={`cursor-pointer p-4 pe-3 flex gap-x-2 sm:w-[172px] w-full h-[66px] justify-start items-center text-base font-medium rounded-2xl font-inter dark:bg-sleap-gray-850 bg-white 
                        ${currency?.label === item?.label ? "border-3 dark:border-lightwhite border-sleap-gray-950" : "border dark:border-sleap-gray-600 border-sleap-gray-200"} 
                        text-lightwhite dark:hover:border-sleap-gray-600 dark:hover:bg-sleap-gray-700  hover:bg-sleap-gray-200
                        shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),_0px_1px_2px_-1px_rgba(0,0,0,0.10)] 
                        transition-all`}
                    onClick={() =>
                      handleSelectCurrency(
                        {
                          label: item?.label,
                          value: item?.value,
                        },
                        <CurrencyImage symbol={item?.value} />
                      )
                    }
                  >
                    <CurrencyImage symbol={item?.value} />
                    <span className="flex flex-col">
                      <p className="text-xs font-medium font-inter dark:text-sleap-gray-100 text-sleap-gray-800 mb-0">
                        {item?.name?.slice(0, 12)}
                        {Number(item?.name?.length) > 12 ? "..." : ""}
                      </p>
                      <span className="dark:text-lightwhite text-sleap-gray-950 font-sm font-bold font-inter">
                        {item?.label}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
          {!flatCurrencies.length && !cryptoCurrencies.length ? (
            <div className="flex justify-center items-center h-[50vh]">
              <p className="dark:text-lightwhite text-sleap-gray-950 font-inter text-base">
                {t("Other.noResultFound")}
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </Modal>
  );
};
